import { useState, useEffect, useCallback } from 'react';
import {
  useParams,
  useNavigate,
  generatePath,
  useLocation,
} from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  SidePanel,
  SidePanelType,
  ButtonPseudo,
  SidePanelLoader,
  PermissionEnum,
  ButtonPrimary,
  ResponseError,
  responseErrorsResolver,
  Popup,
  ButtonSecondary,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import {
  clearDocumentSidepanelState,
  deleteIsSuccess,
  onDocumentMove,
} from './store/slices';
import CorporateDocument from '../../../components/new-application/corporate/document-sidepanel';
import {
  useLazyGetCofApplicationDocumentFileQuery,
  useLazyGetCofNewApplicationDocumentQuery,
  useRenameCofDocumentMutation,
  useChangeCOFApplicationDocumentStatusMutation,
  useChangeCOFApplicationDocumentCategoryMutation,
  useBatchDeleteCOFApplicationDocumentsMutation,
  useChangeNewApplicationDocumentExpirationDateMutation,
} from '../store/services';
import { useAddCustomersCommentMutation } from '../../../store/documents/services';
import { CustomerType, NewApplicationStatus } from '../../customers/types';
import { ApplicationDocument, ApplicationDocumentStatus } from '../types';

import ROUTES from '../../../routes';

import cn from '../new-application.module.css';

const CofDocumentSidepanel = () => {
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [imgLink, setImgLink] = useState('');
  const [documentName, setDocumentName] = useState('');
  const [nameError, setNameError] = useState('');
  const [deletePopupVisible, setDeletePopupVisible] = useState(false);
  const [changeDateError, setChangeDateError] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<
    ApplicationDocumentStatus | undefined
  >(undefined);

  const { applicationId, documentId } = useParams();
  const { search } = useLocation();

  const {
    setError,
    formState: { errors },
  } = useForm();

  const {
    newApplications: {
      mainRepresentative: {
        document: documentItem,
        newApplicationInfo,
        allowedDocumentsCategories,
        documents,
      },
    },
    customersDictionaries: { activePermissions },
  } = useAppSelector((state) => state);

  const [getDocument, { isLoading: documentIsLoading }] =
    useLazyGetCofNewApplicationDocumentQuery();
  const [getDocumentFile, { isLoading, isFetching }] =
    useLazyGetCofApplicationDocumentFileQuery();
  const [addComment, { isLoading: commentInProgress }] =
    useAddCustomersCommentMutation();
  const [rename, { isLoading: renameInProgress }] =
    useRenameCofDocumentMutation();
  const [
    changeCofDocumentStatus,
    { isLoading: statusChangeInProgress, isSuccess },
  ] = useChangeCOFApplicationDocumentStatusMutation();
  const [changeCofDocumentCategory, { isLoading: moveInProgress }] =
    useChangeCOFApplicationDocumentCategoryMutation();
  const [deleteCofAppicationDocuments, { isLoading: deleteInProgress }] =
    useBatchDeleteCOFApplicationDocumentsMutation();
  const [changeDocumentExpDate, { isLoading: updateInProgress }] =
    useChangeNewApplicationDocumentExpirationDateMutation();

  const getDocumentFileAction = async () => {
    const response = await getDocumentFile({
      id: applicationId as string,
      documentId: documentId as string,
    });

    if (response.data) {
      const url = window.URL || window.webkitURL;

      // @ts-ignore
      const blob = url.createObjectURL(response.data);
      setImgLink(blob);
    }
  };

  const onRename = async () => {
    if (documentName.length < 3) {
      setNameError('name must be longer than or equal to 3 characters');
    } else {
      setNameError('');

      const response = (await rename({
        id: applicationId as string,
        documentId: documentId as string,
        body: {
          name: documentName,
        },
      })) as { error?: ResponseError };

      if (response.error) {
        responseErrorsResolver({
          errors: response.error,
          setError,
        });
      }
    }
  };

  const onDownload = () => {
    if (imgLink) {
      const hiddenElement = document.createElement('a');
      // @ts-ignore
      hiddenElement.href = imgLink;
      hiddenElement.target = '_blank';
      hiddenElement.download = `${documentItem?.name}`;
      hiddenElement.click();
    }
  };

  const addCommentAction = (text: string) => {
    addComment({
      id: documentId as string,
      body: {
        text,
      },
    });
  };

  const navigate = useNavigate();

  const dispatch = useAppDispatch();

  const onDelete = async () => {
    const response = await deleteCofAppicationDocuments({
      id: applicationId as string,
      documentId: [documentId as string],
    });

    if (response) {
      setDeletePopupVisible(false);

      const nextDocuments = documents?.filter((item) => item.id !== documentId);

      dispatch(deleteIsSuccess(nextDocuments as ApplicationDocument[]));
    }
  };

  const onChangeDocumentExpDate = async (date: string) => {
    const response = (await changeDocumentExpDate({
      id: documentId as string,
      body: {
        id: documentId as string,
        expirationDate: date,
      },
    })) as { error?: ResponseError | { data: { message: string } } };

    if (response.error) {
      if (Array.isArray(response?.error?.data?.message)) {
        if (response?.error?.data?.message[0]?.property === 'coreError') {
          setChangeDateError(
            response?.error?.data?.message[0]?.value as string,
          );
        }
      } else if (typeof response?.error?.data?.message === 'string') {
        setChangeDateError(response?.error?.data?.message);
      } else {
        setChangeDateError('Internal Server Error. Try Again');
      }
    }
  };

  const changeIndividualDocumentCategoryAction = async (
    documentCategoryId: string,
  ) => {
    const response = await changeCofDocumentCategory({
      id: applicationId as string,
      body: {
        ids: [documentId as string],
        documentCategoryId,
      },
    });

    if (response) {
      dispatch(onDocumentMove(documentCategoryId));
    }
  };

  const handleClose = useCallback(() => {
    setPanelIsOpen(false);
    setDocumentName('');
    setNameError('');

    setTimeout(() => {
      dispatch(clearDocumentSidepanelState());

      const link = ROUTES.CUSTOMERS_NEW_APPLICATION_COMPANY_OFFICIAL;

      const linkString = generatePath(link, {
        applicationId: applicationId as string,
      });

      navigate(`${linkString}${search || ''}`);
    }, 100);
  }, []);

  useEffect(() => {
    if (documentItem) {
      setDocumentName(documentItem.name);
    }
  }, [documentItem]);

  useEffect(() => {
    if (isSuccess) {
      setSelectedStatus(undefined);
    }
  }, [isSuccess]);

  useEffect(() => {
    setPanelIsOpen(true);

    if (documentId && applicationId) {
      getDocument({ id: applicationId, documentId });
      getDocumentFileAction();
    }
  }, [dispatch, documentId, applicationId]);

  let content;

  if (documentIsLoading) {
    content = <SidePanelLoader />;
  } else {
    content = (
      <CorporateDocument
        document={documentItem}
        type={documentItem?.category}
        documentFileIsLoading={isLoading}
        onDownload={onDownload}
        downloadInProgress={isFetching}
        applicationStatus={newApplicationInfo?.status}
        imgLink={imgLink}
        addCommentAction={addCommentAction}
        commentsVisible={activePermissions?.includes(
          PermissionEnum.DOCUMENTS_COMMENTS_ACTION,
        )}
        commentInProgress={commentInProgress}
        clientType={CustomerType.INDIVIDUAL}
        setDocumentName={setDocumentName}
        documentName={documentName}
        nameError={nameError || (errors?.name?.message as string)}
        renameAvailable={activePermissions?.includes(
          PermissionEnum.APPLICATIONS_CUSTOMERS_DOCUMENTS_EDIT_ACTION,
        )}
        updateAllowed={
          activePermissions?.includes(PermissionEnum.DOCUMENTS_UPDATE_ACTION) &&
          (newApplicationInfo?.applicationScheme?.id ===
            'a87db4b4-3c78-4564-bba1-c16a19553159' ||
            newApplicationInfo?.applicationScheme?.id ===
              'a87db4b4-3c78-4564-bba1-c16a19553159')
        }
        onChangeDocumentExpDate={(date) =>
          newApplicationInfo?.applicationScheme?.id ===
            'a87db4b4-3c78-4564-bba1-c16a19553159' ||
          newApplicationInfo?.applicationScheme?.id ===
            'a87db4b4-3c78-4564-bba1-c16a19553159'
            ? onChangeDocumentExpDate(date)
            : undefined
        }
        moveInProgress={moveInProgress}
        setDeletePopupVisible={setDeletePopupVisible}
        onChangeCategory={changeIndividualDocumentCategoryAction}
        changeDateError={changeDateError}
        updateInProgress={updateInProgress}
        allowedCategories={allowedDocumentsCategories}
        regulatedCompanyScheme={
          newApplicationInfo?.applicationScheme?.id ===
          'a87db4b4-3c78-4564-bba1-c16a19553159'
        }
        cofWithDocuments={Boolean(
          newApplicationInfo?.applicationScheme?.id ===
          'ad811a02-87ed-4e19-ad45-770c0fd8fe41',
        )}
      />
    );
  }

  return (
    <>
      <Popup
        title={`Delete ${documentItem?.name}`}
        onCancelClick={() => setDeletePopupVisible(false)}
        onOkClick={onDelete}
        cancelButtonText="Cancel"
        okButtonText={deleteInProgress ? 'Loading...' : 'Delete'}
        isVisible={deletePopupVisible}
        isLoading={deleteInProgress}
        parent={document.getElementById('delete-popup-wrapper') as HTMLElement}
      />
      <SidePanel
        type={SidePanelType.Customers}
        closeOnClickOutside
        isOpen={panelIsOpen}
        onClose={handleClose}
        title={documentItem?.name || ''}
        sidepanelExpandedKey="application-document"
        footer={
          <>
            {newApplicationInfo?.status !== NewApplicationStatus.ACCEPTED &&
              newApplicationInfo?.status !== NewApplicationStatus.DECLINED &&
              documentItem && (
                <>
                  <ButtonPrimary
                    text={renameInProgress ? 'Loading...' : 'Save'}
                    onClick={onRename}
                    disabled={documentItem?.name === documentName}
                    bold
                  />
                  {documentItem?.status !==
                    ApplicationDocumentStatus.ACCEPTED &&
                    documentItem?.status !==
                      ApplicationDocumentStatus.EXPIRED &&
                    !documentItem?.documentCategoryId?.includes(
                      'identification',
                    ) &&
                    newApplicationInfo?.applicationScheme?.id ===
                      'a87db4b4-3c78-4564-bba1-c16a19553159' && (
                      <ButtonSecondary
                        text={
                          statusChangeInProgress &&
                          selectedStatus === ApplicationDocumentStatus.ACCEPTED
                            ? 'Loading...'
                            : 'Accept'
                        }
                        onClick={() => {
                          setSelectedStatus(ApplicationDocumentStatus.ACCEPTED);

                          changeCofDocumentStatus({
                            id: applicationId as string,
                            body: {
                              ids: [documentItem?.id],
                              status: ApplicationDocumentStatus.ACCEPTED,
                            },
                          });
                        }}
                        disabled={statusChangeInProgress}
                        bold
                      />
                    )}
                  {documentItem?.status !==
                    ApplicationDocumentStatus.REJECTED &&
                    documentItem?.status !==
                      ApplicationDocumentStatus.EXPIRED &&
                    !documentItem?.documentCategoryId?.includes(
                      'identification',
                    ) &&
                    newApplicationInfo?.applicationScheme?.id ===
                      'a87db4b4-3c78-4564-bba1-c16a19553159' && (
                      <ButtonSecondary
                        text={
                          statusChangeInProgress &&
                          selectedStatus === ApplicationDocumentStatus.REJECTED
                            ? 'Loading...'
                            : 'Reject'
                        }
                        className={cn.rejectStatusButton}
                        onClick={() => {
                          setSelectedStatus(ApplicationDocumentStatus.REJECTED);

                          changeCofDocumentStatus({
                            id: applicationId as string,
                            body: {
                              ids: [documentItem?.id],
                              status: ApplicationDocumentStatus.REJECTED,
                            },
                          });
                        }}
                        disabled={statusChangeInProgress}
                        bold
                      />
                    )}
                </>
              )}
            <ButtonPseudo
              text="Close"
              onClick={handleClose}
              className={`${cn.closeDocumentSidepanel} button-white`}
              bold
            />
          </>
        }
      >
        {content}
      </SidePanel>
      <div id="delete-popup-wrapper" />
    </>
  );
};

export default CofDocumentSidepanel;
